import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { User, Debt } from '../../types';
import * as usersApi from '../../services/api/usersApi';

interface UsersState {
  users: User[];
  loading: boolean;
  error: string | null;
  selectedUser: User | null;
  selectedUserDebts: Debt[];
  debtsLoading: boolean;
  debtsError: string | null;
}

const initialState: UsersState = {
  users: [],
  loading: false,
  error: null,
  selectedUser: null,
  selectedUserDebts: [],
  debtsLoading: false,
  debtsError: null,
};

// Async thunks
export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState() as any;
      const userId = state.auth.userId;
      if (!userId) {
        return rejectWithValue('User ID not found. Please log in again.');
      }
      return await usersApi.getUsers(userId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch users');
    }
  }
);

export const fetchUserDebts = createAsyncThunk(
  'users/fetchUserDebts',
  async (userId: number, { rejectWithValue }) => {
    try {
      return await usersApi.getUserDebts(userId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch user debts');
    }
  }
);

export const uploadUsersFile = createAsyncThunk(
  'users/uploadUsersFile',
  async (file: File, { rejectWithValue }) => {
    try {
      await usersApi.uploadUsers(file);
      return true;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to upload users');
    }
  }
);

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setSelectedUser: (state, action: PayloadAction<User | null>) => {
      state.selectedUser = action.payload;
      if (!action.payload) {
        state.selectedUserDebts = [];
      }
    },
    clearError: (state) => {
      state.error = null;
      state.debtsError = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch users
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch user debts
    builder
      .addCase(fetchUserDebts.pending, (state) => {
        state.debtsLoading = true;
        state.debtsError = null;
      })
      .addCase(fetchUserDebts.fulfilled, (state, action) => {
        state.debtsLoading = false;
        state.selectedUserDebts = action.payload;
      })
      .addCase(fetchUserDebts.rejected, (state, action) => {
        state.debtsLoading = false;
        state.debtsError = action.payload as string;
      });

    // Upload users file
    builder
      .addCase(uploadUsersFile.fulfilled, (state) => {
        // Optionally refresh users list after upload
      });
  },
});

export const { setSelectedUser, clearError } = usersSlice.actions;
export default usersSlice.reducer;

